import React from 'react';

const Feature = ({ title, desc, icon }) => (
  <div className="relative p-6 rounded-xl bg-white/80 backdrop-blur shadow-sm ring-1 ring-gray-100 hover:shadow-md transition-shadow">
    <div className="flex items-start gap-4">
      <div className="shrink-0 w-10 h-10 rounded-lg bg-purple-50 text-purple-600 flex items-center justify-center">
        {icon}
      </div>
      <div>
        <h3 className="text-sm font-semibold text-gray-900">{title}</h3>
        <p className="mt-1 text-sm text-gray-600">{desc}</p>
      </div>
    </div>
  </div>
);

const About = () => {
  return (
    <main className="relative overflow-hidden">
      {/* Decorative gradient background */}
      <div aria-hidden className="pointer-events-none absolute inset-0 -z-10">
        <div className="absolute -top-40 left-1/2 -translate-x-1/2 h-[480px] w-[980px] rounded-full bg-gradient-to-r from-purple-200 via-fuchsia-200 to-indigo-200 blur-3xl opacity-60" />
        <svg className="absolute bottom-0 left-0 w-full text-gray-50" viewBox="0 0 1440 180" preserveAspectRatio="none">
          <path fill="currentColor" d="M0,64L80,80C160,96,320,128,480,160C640,192,800,224,960,213.3C1120,203,1280,149,1360,122.7L1440,96L1440,320L1360,320C1280,320,1120,320,960,320C800,320,640,320,480,320C320,320,160,320,80,320L0,320Z" />
        </svg>
      </div>

      {/* Hero */}
      <section className="relative">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-24">
          <div className="grid lg:grid-cols-2 gap-10 items-center">
            <div>
              <span className="inline-flex items-center gap-2 rounded-full bg-purple-50 px-3 py-1 text-xs font-semibold text-purple-700 ring-1 ring-purple-200">
                <span className="w-1.5 h-1.5 rounded-full bg-purple-500" />
                About ModStack
              </span>
              <h1 className="mt-4 text-4xl sm:text-5xl font-bold tracking-tight text-gray-900">
                Build interfaces faster with reusable components
              </h1>
              <p className="mt-4 text-base text-gray-600 leading-relaxed">
                ModStack is a growing library of beautiful, production-ready UI components. Browse, remix, and ship
                without reinventing the wheel. Preview every state, copy the code, and stay in your flow.
              </p>
              <div className="mt-6 flex flex-wrap items-center gap-3">
                <a href="/components" className="inline-flex items-center gap-2 rounded-md bg-purple-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-purple-700">
                  Explore Components
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="size-4"><path fillRule="evenodd" d="M12.293 3.293a1 1 0 011.414 0l4.999 5a1 1 0 010 1.414l-5 5a1 1 0 11-1.414-1.414L14.586 11H3a1 1 0 110-2h11.586l-3.293-3.293a1 1 0 010-1.414z" clipRule="evenodd" /></svg>
                </a>
                <a href="/library" className="inline-flex items-center gap-2 rounded-md px-4 py-2 text-sm font-semibold text-purple-700 ring-1 ring-purple-200 hover:bg-purple-50">
                  Your Library
                </a>
              </div>
              <div className="mt-6 flex items-center gap-6 text-sm text-gray-500">
                <div className="flex items-center gap-2"><span className="font-semibold text-gray-900">Zero</span> vendor lock-in</div>
                <div className="flex items-center gap-2"><span className="font-semibold text-gray-900">Copy</span> and customize</div>
              </div>
            </div>
            <div className="relative">
              <div className="absolute -inset-4 -z-10 rounded-2xl bg-gradient-to-tr from-purple-100 to-indigo-100 blur-xl" />
              <div className="rounded-2xl border border-gray-100 bg-white shadow-sm overflow-hidden">
                <div className="px-4 py-2 bg-gray-50 border-b border-gray-100 flex items-center gap-1">
                  <span className="w-2 h-2 rounded-full bg-red-400" />
                  <span className="w-2 h-2 rounded-full bg-yellow-400" />
                  <span className="w-2 h-2 rounded-full bg-green-400" />
                </div>
                <div className="p-6">
                  <div className="grid grid-cols-3 gap-4">
                    {['Buttons', 'Inputs', 'Cards', 'Forms', 'Tabs', 'Badges'].map((label) => (
                      <div key={label} className="aspect-video rounded-lg border border-gray-100 bg-gradient-to-br from-white to-gray-50 grid place-items-center text-sm text-gray-600">
                        {label}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="relative py-12 sm:py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            <Feature
              title="Live preview"
              desc="See every component in action with real states and responsive behavior."
              icon={<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="size-5"><path d="M3.75 5.25A2.25 2.25 0 016 3h12a2.25 2.25 0 012.25 2.25v13.5A2.25 2.25 0 0118 21H6a2.25 2.25 0 01-2.25-2.25V5.25zM6 4.5a.75.75 0 00-.75.75v13.5c0 .414.336.75.75.75h12a.75.75 0 00.75-.75V5.25a.75.75 0 00-.75-.75H6z"/><path d="M7.5 6.75h9a.75.75 0 010 1.5h-9a.75.75 0 010-1.5z"/></svg>}
            />
            <Feature
              title="Copy & paste code"
              desc="Grab clean HTML, CSS, and JS. No hidden dependencies—just ship it."
              icon={<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="size-5"><path d="M15.75 3A2.25 2.25 0 0118 5.25V6h.75A2.25 2.25 0 0121 8.25v10.5A2.25 2.25 0 0118.75 21H8.25A2.25 2.25 0 016 18.75V18H5.25A2.25 2.25 0 013 15.75V5.25A2.25 2.25 0 015.25 3h10.5zM6 18.75c0 .414.336.75.75.75H18.75a.75.75 0 00.75-.75V8.25a.75.75 0 00-.75-.75H18v8.25A2.25 2.25 0 0115.75 18H6v.75z"/></svg>}
            />
            <Feature
              title="Composable"
              desc="Mix and match atoms, molecules, and organisms to design faster."
              icon={<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="size-5"><path d="M11.7 1.2a1 1 0 01.6 0l8.4 3a1 1 0 01.7.95v7.6a6.9 6.9 0 01-3.43 5.98l-5.78 3.29a1 1 0 01-.98 0L5.43 18.73A6.9 6.9 0 012 12.75V5.15a1 1 0 01.7-.95l9-3z"/></svg>}
            />
            <Feature
              title="Accessible"
              desc="Components follow a11y best practices and work with screen readers."
              icon={<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="size-5"><path d="M12 2a2.25 2.25 0 110 4.5A2.25 2.25 0 0112 2z"/><path fillRule="evenodd" d="M3 9.75a.75.75 0 01.75-.75h16.5a.75.75 0 010 1.5H14.5v10a.75.75 0 01-1.5 0V15h-2v5.5a.75.75 0 01-1.5 0v-10H3.75A.75.75 0 013 9.75z" clipRule="evenodd"/></svg>}
            />
            <Feature
              title="Themeable"
              desc="Tailwind-powered styles to match your brand with ease."
              icon={<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="size-5"><path d="M3 5.25A2.25 2.25 0 015.25 3h8.5A2.25 2.25 0 0116 5.25v13.5A2.25 2.25 0 0113.75 21h-8.5A2.25 2.25 0 013 18.75V5.25zM18 6a.75.75 0 01.75-.75h.5A2.75 2.75 0 0122 8v8a2.75 2.75 0 01-2.75 2.75h-.5A.75.75 0 0118 18V6z"/></svg>}
            />
            <Feature
              title="Private libraries"
              desc="Save and manage your own curated set of components."
              icon={<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="size-5"><path d="M4.5 5.25A2.25 2.25 0 016.75 3h8.5A2.25 2.25 0 0117.5 5.25v13.5A2.25 2.25 0 0115.25 21h-8.5A2.25 2.25 0 014.5 18.75V5.25zM19.5 6.75a.75.75 0 01.75-.75h.5A1.25 1.25 0 0122 7.25v9.5A1.25 1.25 0 0120.75 18H20a.75.75 0 01-.75-.75v-10.5z"/></svg>}
            />
          </div>
        </div>
      </section>

      {/* Story / Mission */}
      <section className="relative py-16 sm:py-20">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-2xl sm:text-3xl font-bold text-gray-900">Our mission</h2>
          <p className="mt-4 text-gray-600 leading-relaxed">
            Empower designers and developers to prototype and ship beautiful interfaces—faster. We believe in
            composability, accessibility, and craftsmanship. ModStack is where reusable meets remarkable.
          </p>
        </div>
      </section>

      {/* CTA */}
      <section className="relative pb-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="relative overflow-hidden rounded-2xl bg-gradient-to-r from-purple-600 to-indigo-600">
            <div className="absolute inset-0 opacity-20">
              <svg className="w-full h-full" xmlns="http://www.w3.org/2000/svg" preserveAspectRatio="none" viewBox="0 0 600 600" fill="none">
                <g opacity=".5" stroke="white">
                  <circle cx="300" cy="300" r="180" />
                  <circle cx="300" cy="300" r="250" />
                </g>
              </svg>
            </div>
            <div className="relative px-8 py-12 sm:px-12 sm:py-14 lg:px-16 lg:py-16 text-white grid lg:grid-cols-2 gap-8 items-center">
              <div>
                <h3 className="text-2xl font-bold">Join the library</h3>
                <p className="mt-2 text-white/90">Create your private component collection and speed up your next project.</p>
              </div>
              <div className="flex gap-3 lg:justify-end">
                <a href="/register" className="inline-flex items-center gap-2 rounded-md bg-white text-gray-900 px-4 py-2 text-sm font-semibold shadow-sm hover:bg-gray-100">Get Started</a>
                <a href="/components" className="inline-flex items-center gap-2 rounded-md ring-1 ring-white/60 px-4 py-2 text-sm font-semibold text-white/95 hover:bg-white/10">Browse Components</a>
              </div>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
};

export default About;
